import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { User, Building2, Building, ArrowRight } from 'lucide-react';

const useCases = [
  {
    icon: User,
    title: 'Individual Counselors',
    description: 'Solo practitioners who want to focus on therapy, not paperwork. AI handles documentation, scheduling, and billing while you focus on your patients.',
    features: [
      'Auto-generated session notes save 2+ hours daily',
      'Real-time AI guidance enhances clinical quality',
      'Integrated telehealth for hybrid practice',
      'Simple billing with automated claim generation',
    ],
    cta: 'Start Free Trial',
    color: 'from-brand-500 to-brand-600',
    bgColor: 'bg-brand-50',
  },
  {
    icon: Building2,
    title: 'Clinics & Group Practices',
    description: 'Multi-provider practices that need consistency, efficiency, and oversight across their entire care team with minimal operational disruption.',
    features: [
      'Standardized documentation across all providers',
      'AI-powered patient-counselor matching',
      'Practice-wide analytics and outcome reporting',
      'Custom branding and workflow configuration',
    ],
    cta: 'Book A Demo',
    color: 'from-accent-500 to-emerald-600',
    bgColor: 'bg-accent-50',
  },
  {
    icon: Building,
    title: 'Organizations & BHI Programs',
    description: 'Healthcare organizations implementing Behavioral Health Integration with primary care practices, needing scalable AI-powered care coordination.',
    features: [
      'CMS BHI compliance built in',
      'Managed services with embedded care navigators',
      'Multi-location deployment and management',
      'Bilingual patient engagement tools',
    ],
    cta: 'Contact Sales',
    color: 'from-violet-500 to-purple-600',
    bgColor: 'bg-violet-50',
  },
];

export default function UseCases() {
  const { ref, inView } = useScrollAnimation();

  return (
    <section id="use-cases" className="section-padding bg-gradient-to-b from-white to-gray-50" ref={ref}>
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4 tracking-tight">
            Built for Every{' '}
            <span className="gradient-text">Practice Size</span>
          </h2>
          <p className="text-lg text-gray-600">
            From solo practitioners to enterprise organizations,
            Doctorite AI scales with your needs.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {useCases.map((useCase, index) => (
            <motion.div
              key={useCase.title}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              className="relative rounded-2xl overflow-hidden shadow-lg shadow-brand-900/5 border border-gray-100 flex flex-col"
            >
              <div className="bg-white p-8 flex-1">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${useCase.color} flex items-center justify-center mb-5 shadow-lg`}>
                  <useCase.icon className="w-7 h-7 text-white" />
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-3">{useCase.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">{useCase.description}</p>

                <ul className="space-y-3">
                  {useCase.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm">
                      <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${useCase.color} flex items-center justify-center shrink-0 mt-0.5`}>
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="px-8 pb-8 bg-white">
                <Link
                  to="/contact"
                  className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-xl font-semibold text-sm transition-all duration-200 group bg-brand-50 text-brand-700 hover:bg-brand-100"
                >
                  {useCase.cta}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
